import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import {
  STAGE_META, STAGES, OPEN_STAGES, PRIORITY_META, CATEGORIES, CLASSIFICATIONS, METHODS, OUTCOMES, CLUB_OPTIONS,
  Pill, FollowUpPill, money, shortDate, todayISO,
} from './esMeta';

// Side panel for one deal: log a contact (with a follow-up for the person contacted), the account's people and their
// own follow-ups, the deal's details, and the account's full activity. Every real contact on an open deal must leave a
// follow-up date or a reason none is needed (BK, 10/8/26).

const label = { display: 'block', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, margin: '8px 0 3px' };
const section = { background: '#181818', border: '1px solid #2a2a2a', borderRadius: 8, padding: '10px 12px', marginBottom: 12 };
const sectionTitle = { fontSize: 11, fontWeight: 700, color: '#a8a496', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };

function Field({ title, children }) {
  return <div><span style={label}>{title}</span>{children}</div>;
}

function LogContactForm({ deal, contacts, onSaved }) {
  const { post } = useApi();
  const active = contacts.filter(c => c.active);
  const [f, setF] = useState({
    contact_id: active[0]?.contact_id || '', method: 'call', outcome: '', notes: '', stage: '',
    lost_reason: '', follow_up_date: '', follow_up_note: '', no_follow_up: false, no_follow_up_reason: '', next_action: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const isNote = f.method === 'note';
  const finalStage = f.stage || deal.stage;
  const needsFollowUp = !isNote && OPEN_STAGES.includes(finalStage);

  async function save() {
    setError('');
    if (!isNote && !f.contact_id) return setError('Pick who you contacted.');
    if (!isNote && !f.outcome) return setError('Pick an outcome.');
    if (isNote && !f.notes.trim()) return setError('Write the note.');
    if (needsFollowUp && !f.follow_up_date && !(f.no_follow_up && f.no_follow_up_reason.trim())) {
      return setError('Set a follow-up date, or tick "No follow-up needed" and say why.');
    }
    if (f.stage === 'closed_lost' && !f.lost_reason.trim()) return setError('Say why it was lost.');
    setSaving(true);
    try {
      await post(`/es/deals/${deal.deal_id}/log`, {
        contact_id: f.contact_id || null, method: f.method, outcome: isNote ? null : f.outcome, notes: f.notes,
        stage: f.stage || null, lost_reason: f.lost_reason,
        follow_up_date: f.no_follow_up ? null : f.follow_up_date, follow_up_note: f.follow_up_note,
        no_follow_up_reason: f.no_follow_up ? f.no_follow_up_reason : null, next_action: f.next_action,
      });
      setF(prev => ({ ...prev, outcome: '', notes: '', stage: '', lost_reason: '', follow_up_date: '', follow_up_note: '', no_follow_up: false, no_follow_up_reason: '', next_action: '' }));
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={section}>
      <div style={sectionTitle}>Log contact</div>
      {active.length === 0 && !isNote && (
        <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 4 }}>Add a contact person below first (or log a note).</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
        <Field title="Who">
          <select className="field-input" value={f.contact_id} onChange={e => set('contact_id', e.target.value)}>
            <option value="">—</option>
            {active.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}{c.title ? ` (${c.title})` : ''}</option>)}
          </select>
        </Field>
        <Field title="How">
          <select className="field-input" value={f.method} onChange={e => set('method', e.target.value)}>
            {Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        {!isNote && (
          <Field title="Outcome">
            <select className="field-input" value={f.outcome} onChange={e => set('outcome', e.target.value)}>
              <option value="">—</option>
              {Object.entries(OUTCOMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        )}
        <Field title="Move stage to">
          <select className="field-input" value={f.stage} onChange={e => set('stage', e.target.value)}>
            <option value="">Keep: {STAGE_META[deal.stage].label}</option>
            {STAGES.filter(s => s !== deal.stage).map(s => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
          </select>
        </Field>
      </div>
      {f.stage === 'closed_lost' && (
        <Field title="Why lost">
          <input className="field-input" value={f.lost_reason} onChange={e => set('lost_reason', e.target.value)} placeholder="Budget, timing, went elsewhere..." />
        </Field>
      )}
      <Field title="Notes">
        <textarea className="field-input" rows={3} value={f.notes} onChange={e => set('notes', e.target.value)} />
      </Field>

      {needsFollowUp && (
        <div style={{ marginTop: 6, padding: '8px 10px', border: '1px solid #3b82f655', borderRadius: 6, background: '#3b82f60d' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#93c5fd' }}>
            Next follow-up{f.contact_id ? ` with ${active.find(c => String(c.contact_id) === String(f.contact_id))?.name || 'this contact'}` : ''}
          </div>
          {!f.no_follow_up && (
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8 }}>
              <Field title="Date">
                <input type="date" className="field-input" min={todayISO()} value={f.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} />
              </Field>
              <Field title="What to do">
                <input className="field-input" value={f.follow_up_note} onChange={e => set('follow_up_note', e.target.value)} placeholder="Send revised proposal, call back..." />
              </Field>
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#aaa', marginTop: 6 }}>
            <input type="checkbox" checked={f.no_follow_up} onChange={e => set('no_follow_up', e.target.checked)} />
            No follow-up needed
          </label>
          {f.no_follow_up && (
            <input className="field-input" style={{ marginTop: 4 }} value={f.no_follow_up_reason} onChange={e => set('no_follow_up_reason', e.target.value)} placeholder="Why (e.g. they will call us after their board meets)" />
          )}
        </div>
      )}

      {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save contact'}</button>
      </div>
    </div>
  );
}

function ContactsSection({ accountId, contacts, onChanged }) {
  const { post, put } = useApi();
  const [adding, setAdding] = useState(false);
  const [c, setC] = useState({ name: '', title: '', email: '', phone: '' });
  const [error, setError] = useState('');

  async function add() {
    setError('');
    if (!c.name.trim()) return setError('Name required.');
    try {
      await post(`/es/accounts/${accountId}/contacts`, c);
      setC({ name: '', title: '', email: '', phone: '' });
      setAdding(false);
      onChanged();
    } catch (e) { setError(e.message); }
  }
  async function update(contactId, body) {
    try { await put(`/es/contacts/${contactId}`, body); onChanged(); } catch (e) { setError(e.message); }
  }

  return (
    <div style={section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={sectionTitle}>People ({contacts.filter(x => x.active).length})</div>
        <button className="btn btn-ghost btn-sm" onClick={() => setAdding(a => !a)}>{adding ? 'Cancel' : '+ Add person'}</button>
      </div>
      {contacts.filter(x => x.active).map(p => (
        <div key={p.contact_id} style={{ borderTop: '1px solid #242424', padding: '6px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>
              {p.name}{p.title && <span style={{ color: '#888', fontWeight: 400 }}> · {p.title}</span>}
              {p.is_primary && <span style={{ marginLeft: 6 }}><Pill color="#a8a496">Primary</Pill></span>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {!p.is_primary && <button className="btn btn-ghost btn-sm" style={{ fontSize: 9.5 }} onClick={() => update(p.contact_id, { is_primary: true })}>Make primary</button>}
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 9.5 }}
                onClick={() => window.confirm(`Remove ${p.name} from this account? Their history stays.`) && update(p.contact_id, { active: false })}>Remove</button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#aaa' }}>
            {p.phone && <a href={`tel:${p.phone}`} style={{ color: '#aaa', marginRight: 10 }}>{p.phone}</a>}
            {p.email && <a href={`mailto:${p.email}`} style={{ color: '#aaa' }}>{p.email}</a>}
          </div>
          <div style={{ marginTop: 3, fontSize: 10.5, color: '#888' }}>
            {p.last_contact_at ? `Last contact ${shortDate(p.last_contact_at)}` : 'Not contacted yet'}{' '}
            <FollowUpPill date={p.follow_up_date} overdue={p.follow_up_overdue} />
            {!p.follow_up_date && p.no_follow_up_reason && <span title={p.no_follow_up_reason}>· no follow-up: {p.no_follow_up_reason}</span>}
          </div>
        </div>
      ))}
      {adding && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px', marginTop: 6 }}>
          <Field title="Name"><input className="field-input" value={c.name} onChange={e => setC({ ...c, name: e.target.value })} /></Field>
          <Field title="Title"><input className="field-input" value={c.title} onChange={e => setC({ ...c, title: e.target.value })} /></Field>
          <Field title="Email"><input className="field-input" value={c.email} onChange={e => setC({ ...c, email: e.target.value })} /></Field>
          <Field title="Phone"><input className="field-input" value={c.phone} onChange={e => setC({ ...c, phone: e.target.value })} /></Field>
          <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={add}>Add</button>
          </div>
        </div>
      )}
      {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

function DetailsSection({ deal, account, onSaved }) {
  const { put } = useApi();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setD({
      name: deal.name || '', location_id: deal.location_id || '', priority: deal.priority || '', value: deal.value ?? '',
      classification: deal.classification, what_they_get: deal.what_they_get || '',
      term_start: deal.term_start ? String(deal.term_start).slice(0, 10) : '', term_end: deal.term_end ? String(deal.term_end).slice(0, 10) : '',
      event_date: deal.event_date ? String(deal.event_date).slice(0, 10) : '', event_time: deal.event_time ? String(deal.event_time).slice(0, 5) : '',
      courts_used: deal.courts_used ?? '', est_attendees: deal.est_attendees ?? '', next_action: deal.next_action || '',
      rep_name: deal.rep_name || '', notes: deal.notes || '', category: account?.category || '',
    });
  }, [deal, account]);
  if (!d) return null;
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));
  const isEvent = deal.deal_type === 'event';

  async function save() {
    setError('');
    try {
      const { category, ...dealFields } = d;
      await put(`/es/deals/${deal.deal_id}`, dealFields);
      if ((account?.category || '') !== category) await put(`/es/accounts/${deal.account_id}`, { category: category || null });
      onSaved();
    } catch (e) { setError(e.message); }
  }

  return (
    <div style={section}>
      <div style={sectionTitle}>Details</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
        <div style={{ gridColumn: 'span 2' }}><Field title={isEvent ? 'Event name' : 'Deal name'}><input className="field-input" value={d.name} onChange={e => set('name', e.target.value)} /></Field></div>
        <Field title="Club">
          <select className="field-input" value={d.location_id} onChange={e => set('location_id', e.target.value)}>
            <option value="">Multi-club / not set</option>
            {CLUB_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field title="Priority">
          <select className="field-input" value={d.priority} onChange={e => set('priority', e.target.value)}>
            <option value="">—</option>
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field title="Category">
          <select className="field-input" value={d.category} onChange={e => set('category', e.target.value)}>
            <option value="">—</option>
            {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field title={isEvent ? 'Price ($)' : 'Value per year ($)'}><input className="field-input" value={d.value} onChange={e => set('value', e.target.value)} /></Field>
        {!isEvent && (
          <>
            <Field title="Type">
              <select className="field-input" value={d.classification} onChange={e => set('classification', e.target.value)}>
                {Object.entries(CLASSIFICATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field title="Term ends"><input type="date" className="field-input" value={d.term_end} onChange={e => set('term_end', e.target.value)} /></Field>
            <Field title="Term starts"><input type="date" className="field-input" value={d.term_start} onChange={e => set('term_start', e.target.value)} /></Field>
            <div style={{ gridColumn: 'span 2' }}><Field title="What they get"><input className="field-input" value={d.what_they_get} onChange={e => set('what_they_get', e.target.value)} /></Field></div>
          </>
        )}
        {isEvent && (
          <>
            <Field title="Event date"><input type="date" className="field-input" value={d.event_date} onChange={e => set('event_date', e.target.value)} /></Field>
            <Field title="Start time"><input type="time" className="field-input" value={d.event_time} onChange={e => set('event_time', e.target.value)} /></Field>
            <Field title="Courts"><input className="field-input" value={d.courts_used} onChange={e => set('courts_used', e.target.value)} /></Field>
            <Field title="Expected guests"><input className="field-input" value={d.est_attendees} onChange={e => set('est_attendees', e.target.value)} /></Field>
          </>
        )}
        <Field title="Rep"><input className="field-input" value={d.rep_name} onChange={e => set('rep_name', e.target.value)} /></Field>
        <Field title="Next action"><input className="field-input" value={d.next_action} onChange={e => set('next_action', e.target.value)} /></Field>
        <div style={{ gridColumn: 'span 2' }}><Field title="Notes"><textarea className="field-input" rows={2} value={d.notes} onChange={e => set('notes', e.target.value)} /></Field></div>
      </div>
      {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={save}>Save details</button>
      </div>
    </div>
  );
}

function ActivitySection({ log }) {
  return (
    <div style={section}>
      <div style={sectionTitle}>Activity ({log.length})</div>
      {log.length === 0 && <div style={{ fontSize: 11, color: '#666' }}>Nothing logged yet.</div>}
      {log.map(l => (
        <div key={l.log_id} style={{ borderTop: '1px solid #242424', padding: '6px 0', fontSize: 11.5 }}>
          <div style={{ color: '#888', fontSize: 10.5 }}>
            {new Date(l.logged_at).toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            {' · '}{l.logged_by_name || 'System'}
            {l.deal_name && <span> · {l.deal_name}</span>}
          </div>
          {l.method === 'system' ? (
            <div style={{ color: '#a8a496' }}>
              Stage: {STAGE_META[l.stage_before]?.label || l.stage_before} → {STAGE_META[l.stage_after]?.label || l.stage_after}
              {l.notes && <span> ({l.notes})</span>}
            </div>
          ) : (
            <>
              <div>
                <strong>{METHODS[l.method] || l.method}</strong>
                {l.outcome && <span> · {OUTCOMES[l.outcome] || l.outcome}</span>}
                {l.contact_name && <span style={{ color: '#aaa' }}> · {l.contact_name}</span>}
                {l.stage_after && <span style={{ color: '#a8a496' }}> · moved to {STAGE_META[l.stage_after]?.label}</span>}
              </div>
              {l.notes && <div style={{ color: '#ccc', whiteSpace: 'pre-wrap' }}>{l.notes}</div>}
              {l.follow_up_date && <div style={{ color: '#93c5fd' }}>Follow up {shortDate(l.follow_up_date)}{l.follow_up_note ? `: ${l.follow_up_note}` : ''}</div>}
              {l.no_follow_up_reason && <div style={{ color: '#888' }}>No follow-up: {l.no_follow_up_reason}</div>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default function DealPanel({ dealId, onClose, onChanged }) {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    get(`/es/deals/${dealId}`).then(setData).catch(e => setError(e.message));
  }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };
  const deal = data?.deal;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, maxWidth: '100%', height: '100%', overflowY: 'auto', background: '#141414', borderLeft: '1px solid #2a2a2a', padding: '16px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{deal ? data.account.name : 'Loading...'}</div>
            {deal && (
              <div style={{ marginTop: 4 }}>
                {deal.name !== data.account.name && <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>{deal.name}</div>}
                <Pill color={STAGE_META[deal.stage].color}>{STAGE_META[deal.stage].label}</Pill>
                {deal.priority && <Pill color={PRIORITY_META[deal.priority].color}>{PRIORITY_META[deal.priority].label}</Pill>}
                <Pill color="#6b7280">{deal.location_id || 'Multi-club'}</Pill>
                {deal.value !== null && <Pill color="#22c55e">{money(deal.value)}{deal.deal_type === 'sponsorship' ? '/yr' : ''}</Pill>}
                <FollowUpPill date={deal.follow_up_date} overdue={deal.follow_up_overdue} />
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: 12 }}>{error}</div>}
        {deal && (
          <>
            <LogContactForm key={`${deal.deal_id}-${data.log.length}`} deal={deal} contacts={data.contacts} onSaved={refresh} />
            <ContactsSection accountId={deal.account_id} contacts={data.contacts} onChanged={refresh} />
            <DetailsSection deal={deal} account={data.account} onSaved={refresh} />
            {data.otherDeals.length > 0 && (
              <div style={section}>
                <div style={sectionTitle}>Other deals with {data.account.name}</div>
                {data.otherDeals.map(o => (
                  <div key={o.deal_id} style={{ fontSize: 11.5, padding: '3px 0' }}>
                    {o.deal_type === 'event' ? '📅' : '🤝'} {o.name} <Pill color={STAGE_META[o.stage].color}>{STAGE_META[o.stage].label}</Pill>
                    {o.value && <span style={{ color: '#888' }}>{money(o.value)}</span>}
                  </div>
                ))}
              </div>
            )}
            <ActivitySection log={data.log} />
          </>
        )}
      </div>
    </div>
  );
}
